import {
  Component,
  OnInit,
  OnDestroy,
  SimpleChanges,
  NgZone,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnChanges,
  input,
  output,
  inject,
  effect,
  signal,
} from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { DatePickerModule } from 'primeng/datepicker';
import type {
  Actions,
  AppErrors,
  AutomationActionPayload,
  AutomationMaster,
  CampaignActionsGroup,
  CampaignRequest,
  Client,
  CreateRecurringTaskByClientRequest,
  CreateRecurringTaskByOpportunityRequest,
  CreateTaskByClientRequest,
  CreateTaskByOpportunityRequest,
  NotificationToStaff,
  RepeatRuleResponse,
  ReportDataForCampaign,
  Reports,
  STAFFINTERFACE,
  ScheduleActionPayload,
  SourceDataExtractionRules,
  TENANTSINTERFACE,
  TaskActionPayload,
} from '@fitness-force/models';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CampaignSource,
  OwnershipLevel,
  Roles,
  SendNotificationTo,
  StaffStatus,
  TaskAssignToType,
  TaskCreateType,
  TaskPriority,
  TaskType,
} from '@fitness-force/enum';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorMsgComponent, ErrorsComponent } from '@fitness-force/errors';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputMaskModule } from 'primeng/inputmask';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DividerModule } from 'primeng/divider';
import { ListboxModule } from 'primeng/listbox';

import { COMPANY_INFO, STAFF_STATE, TENANTS_INFO } from '@fitness-force/state';

import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { RruleComponent } from '../../rrule/rrule.component';
import { MiniSearchComponent } from '@fitness-force/common/search';
import {
  TaskCategoryDropdownComponent,
  PriorityComponent,
} from '@fitness-force/crm';

import { BehaviorSubject, distinctUntilChanged, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AuthSevices,
  CommonService,
  TaskService,
  TimeconversionService,
  ToastMessageService,
  CampaignService,
} from '@fitness-force/api';

import { ScrollPanelModule } from 'primeng/scrollpanel';
import { AvatarModule } from 'primeng/avatar';
import { ChipModule } from 'primeng/chip';

import { AutomationScheduleTypeComponent } from '../../automation-schedule-type/automation-schedule-type.component';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-task-create',
  templateUrl: './task-create.component.html',
  styleUrls: ['./task-create.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    StyleClassModule,
    DatePickerModule,
    MultiSelectModule,
    SelectButtonModule,
    InputMaskModule,
    ToggleSwitchModule,
    ScrollPanelModule,
    DividerModule,
    RruleComponent,
    AssignedToComponent,
    MiniSearchComponent,
    ErrorMsgComponent,
    TaskCategoryDropdownComponent,
    PriorityComponent,
    ErrorsComponent,
    AvatarModule,
    ChipModule,
    ListboxModule,
    AutomationScheduleTypeComponent,
    CheckboxModule,
    TranslateModule,
    ToastModule,
    AsyncPipe,
    NgClass,
  ],
})
export class TaskCreateComponent implements OnInit, OnChanges, OnDestroy {
  fb = inject(UntypedFormBuilder);
  private readonly store = inject(Store);
  translate = inject(TranslateService);
  taskService = inject(TaskService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly authService = inject(AuthSevices);
  private readonly ngZone = inject(NgZone);
  cd = inject(ChangeDetectorRef);
  timeService = inject(TimeconversionService);
  campaignService = inject(CampaignService);
  commonService = inject(CommonService);
  private readonly featureFlagService = inject(FeatureFlagService);

  readonly showPopup = input(false);
  readonly clientIds = input<number[]>([]);
  /** Pre-selected full client objects from TaskList - avoids redundant API calls in MiniSearch */
  readonly initialClients = input<any[]>([]);
  localClientIds: number[] = [];
  readonly opportunityIds = input<number[]>([]);
  private readonly destroy$ = new Subject<void>();
  readonly assignedToStaffId = input(0);
  readonly clientGroup = input<any[]>([]);
  readonly opportunity = input<any>();
  readonly clientInfo = input<any>([]);
  readonly taskCreateType = input<TaskCreateType>(TaskCreateType.Single);
  readonly CampaignSource = input<CampaignSource>(CampaignSource.Clients);
  readonly ReportDataForCampaign = input(
    new BehaviorSubject<ReportDataForCampaign>({} as ReportDataForCampaign),
  );
  readonly totalRecords = input(new BehaviorSubject<number>(0));
  readonly selectedAllData = input(new BehaviorSubject<boolean>(false));
  readonly actionPayload = input(
    new BehaviorSubject<TaskActionPayload>({} as TaskActionPayload),
  );
  readonly automationMaster = input(
    new BehaviorSubject<AutomationMaster>({} as AutomationMaster),
  );
  readonly taskCreateformGroup = input<any>(new BehaviorSubject<any>(null));
  readonly uuid = input<string>('');
  readonly notificationTo = input<SendNotificationTo>(
    SendNotificationTo.Client,
  );
  readonly mode = input<string>('create');
  readonly scheduleInMin = input<number>(0);
  //@Output('hide') HidePopup: EventEmitter<{closePopup: boolean, data:TaskActionPayload | null}> = new EventEmitter();
  readonly HidePopup = output<{
    closePopup: boolean;
    data: unknown;
    form: UntypedFormGroup | null;
    uuid: string;
    notificationTo: SendNotificationTo;
  }>();

  readonly closeTaskPopup = output<any>();
  readonly openClientProfile = output<any>();

  clientIds$ = new BehaviorSubject<number[]>([]);

  public clients: Client[] = [];
  showMiniSearch = false;
  showRRule = false;
  tenantId = 0;
  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  rRuleObj: RepeatRuleResponse = {} as RepeatRuleResponse;
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;
  languageCode = '';
  currentUserDetails: any;
  assignedToId = new BehaviorSubject(0);
  showAllMembers = false;
  showCreateProspectPopup$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  selectedTaskcategoryType = new BehaviorSubject<string>('');

  taskTypes: any[] = [];
  taskTypeOptions: any[] = [];
  transalteKey: string[] = [
    'common.TASKTYPE.EMAIL',
    'common.TASKTYPE.SMS',
    'common.TASKTYPE.CALL',
    'common.TASKTYPE.TASK',
    'common.TASKTYPE.TOUR',
  ];
  btnLoading = false;

  taskCategoryId: number = 0;
  priority: string = '';
  showCampaign = new BehaviorSubject<boolean>(false);
  accessedStaffs: STAFFINTERFACE[] = [];
  showClientSidebar = false;
  activeClientId = 0;
  minimumDate: Date;
  showScheduleType = new BehaviorSubject<boolean>(false);
  private readonly isClosing = signal(false);

  constructor() {
    // Angular 21: Use effect in constructor to watch for dialog visibility changes
    effect(() => {
      const isVisible = this.showPopup();
      if (!isVisible) {
        this.isClosing.set(false);
      }
    });

    this.minimumDate = new Date(new Date().setHours(0, 0, 0, 0));
    this.formGroup = this.fb.group({
      selectedClients: new UntypedFormControl('', [Validators.required]),
      selectedTaskCategory: new UntypedFormControl('', [Validators.required]),
      selectedTask: new UntypedFormControl('', [Validators.required]),
      selectedPriority: new UntypedFormControl('', [Validators.required]),
      selectedAssigned: new UntypedFormControl('', [Validators.required]),
      scheduleDate: new UntypedFormControl(new Date(), [Validators.required]),
      scheduleTime: new UntypedFormControl(new Date(), [Validators.required]),
      title: new UntypedFormControl('', [Validators.required]),
      comment: new UntypedFormControl('', [Validators.required]),
      is_visible: [false, [Validators.required]],
      campaign_name: ['', []],
      campaign_action_date: [new Date(), []],
      campaign_description: ['', []],
      automation_timings_type: [''],
      automation_schedule_type: [''],
      automation_timing: [''],
      notify_record_associated_staff: [''],
      notify_additional_staff_to: [],
    });
    this.taskTypes = [
      { name: 'Call', value: 'call', icon: 'fa fa-phone' },
      { name: 'Tour', value: 'tour', icon: 'fa fa-map-marker-alt' },
    ];

    this.taskTypeOptions = this.taskTypes;

    this.setVariables();
  }

  ngOnInit(): void {
    this.setVariables();

    // Use getFlag$() observable to wait for Statsig initialization
    this.featureFlagService
      .getFlag$('show_campaign')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showCampaign.next(isEnabled);
      });

    this.store
      .select(COMPANY_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((responseData) => {
        this.languageCode = responseData?.selectedLang?.LANG_CODE;
        if (responseData?.selectedLang?.LANG_CODE) {
          this.translate.setDefaultLang(responseData.selectedLang.LANG_CODE);
          // Note: translate.use() is deprecated but used throughout codebase - will be migrated in future update
          this.translate
            .use(responseData.selectedLang.LANG_CODE)
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        }
      });

    this.store.select(STAFF_STATE).subscribe((staffs: any) => {
      this.accessedStaffs = staffs.staffList.filter(
        (s: STAFFINTERFACE) =>
          s.role !== Roles.FFAdmin &&
          s.role.toString() !== Roles[Roles.FFAdmin] &&
          (s.status === StaffStatus.Active ||
            s.status.toString() === StaffStatus[StaffStatus.Active]),
      );
    });

    this.currentUserDetails = this.authService.currentUserDetails();
    this.assignedToId.next(this.currentUserDetails.Id);
    const opportunity = this.opportunity();
    if (opportunity?.client_info) {
      this.clientIds$.next([opportunity.client_info.id]);
      this.opportunityIds().push(opportunity.id);
      this.localClientIds = [];
      this.clientIds().push(opportunity.client_info.id);
    }

    this.translate.get(this.transalteKey).subscribe((translations) => {
      this.taskTypes.find(
        (item: { value: string }) => item.value == 'call',
      ).name = translations['common.TASKTYPE.CALL'];
      this.taskTypes.find(
        (item: { value: string }) => item.value == 'tour',
      ).name = translations['common.TASKTYPE.TOUR'];
    });
    this.taskTypeOptions = this.taskTypes;

    this.SelectedTaskCategory.valueChanges.subscribe((o) => {
      if (o?.type && o?.type.toLowerCase() == 'operational') {
        this.taskTypeOptions = this.taskTypes.filter(
          (o: { value: string }) => o.value != 'tour',
        );
        // Only set default if no task type is already selected
        if (!this.SelectedTask.value) {
          this.SelectedTask.setValue(
            this.taskTypes.find((o: { value: string }) => o.value == 'call'),
          );
        }
      } else {
        this.taskTypeOptions = this.taskTypes;
        // Don't clear the task type if it's already set from edit mode
        const actionPayload = this.actionPayload();
        if (!actionPayload?.value?.type) {
          this.SelectedTask.setValue(null);
        }
      }
      this.cd.detectChanges();
    });
    const taskCreateType = this.taskCreateType();
    if (
      taskCreateType == TaskCreateType.Campaign ||
      taskCreateType == TaskCreateType.Automation
    ) {
      this.formGroup.controls['selectedClients'].removeValidators(
        Validators.required,
      );
    }

    if (taskCreateType == TaskCreateType.Automation)
      this.SelectedAssigned.clearValidators();

    const actionPayload = this.actionPayload();
    if (actionPayload?.value?.category_id > 0) {
      // Delay to ensure task types are initialized
      setTimeout(() => {
        this.setInitData();
      }, 50);
    } else {
      this.showScheduleType.next(true);
    }

    this.valueChangeFunction();
  }

  setInitData() {
    const actionPayload = this.actionPayload();
    if (actionPayload.value) {
      // Set task category ID
      this.taskCategoryId = actionPayload.value.category_id;

      // Set priority - handle both string and number formats
      if (typeof actionPayload.value.priority === 'string') {
        this.priority = actionPayload.value.priority;
      } else {
        this.priority =
          this.getEnumStringByNumber(
            TaskPriority,
            Number.parseInt(String(actionPayload.value.priority), 10),
          ) ?? '';
      }

      // Set assigned staff - handle different assignment types
      if (actionPayload.value.assigned_to_staff_id > 0) {
        this.SelectedAssigned.setValue(
          actionPayload.value.assigned_to_staff_id,
        );
      }

      // Set task type - find the matching task type object
      let taskType;
      if (typeof actionPayload.value.type === 'string') {
        // Map string values to task type objects
        const typeValue = actionPayload.value.type.toLowerCase();
        taskType = this.taskTypes.find(
          (o: { value: string }) => o.value.toLowerCase() === typeValue,
        );
      } else {
        const taskTypeName = this.getEnumStringByNumber(
          TaskType,
          Number.parseInt(String(actionPayload.value.type), 10),
        );
        taskType = this.taskTypes.find(
          (o: { name: string }) => o.name === taskTypeName,
        );
      }

      if (taskType) {
        this.SelectedTask.setValue(taskType);
      }

      // Set title and notes
      this.Title.setValue(actionPayload.value.title);
      this.Comment.setValue(actionPayload.value.notes);

      // Set scheduled date and time
      if (actionPayload.value.scheduled_date) {
        const scheduledDate = new Date(actionPayload.value.scheduled_date);
        this.ScheduleDate.setValue(scheduledDate);
        this.ScheduleTime.setValue(scheduledDate);
      }
    }

    const mode = this.mode();
    if (mode === 'update') {
      const scheduleInMin = this.scheduleInMin();
      let scheduleType: string;
      if (scheduleInMin > 0) {
        scheduleType = 'After';
      } else if (scheduleInMin < 0) {
        scheduleType = 'Before';
      } else {
        scheduleType = 'OnEvent';
      }
      const SelectedAutomationTimingType =
        this.commonService.formatScheduleTime(
          scheduleInMin,
          this.automationMaster().value.automation_type.toString(),
        );

      this.SelectedAutomationScheduleTypeFormControl.setValue({
        key: SelectedAutomationTimingType.key,
        value: SelectedAutomationTimingType.key,
      });
      this.SelectedAutomationTimingTypeFormControl.setValue(scheduleType);
      this.formGroup.controls['automation_timing'].setValue({
        key: SelectedAutomationTimingType.name,
        value: SelectedAutomationTimingType.value,
      });

      // Trigger the automation component to update its selected button
      setTimeout(() => {
        // Force the automation component to recognize the timing type change
        this.SelectedAutomationTimingTypeFormControl.updateValueAndValidity();
      }, 200);

      this.formGroup.updateValueAndValidity();
      this.cd.detectChanges();
    }

    // Trigger change detection after setting all values
    setTimeout(() => {
      this.formGroup.updateValueAndValidity();
      this.cd.detectChanges();
    }, 100);

    this.showScheduleType.next(true);
  }

  getEnumStringByNumber(enumType: any, value: number) {
    const keys = Object.keys(enumType).filter((key) => enumType[key] === value);
    return keys.length > 0 ? keys[0] : null;
  }

  valueChangeFunction() {
    this.IsVisibleFormControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value) {
          this.CampaignNameFormControl.setValidators([Validators.required]);
          this.CampaignDescriptionFormControl.setValidators([
            Validators.required,
          ]);
          this.CampaignActionDateFormControl.setValidators([
            Validators.required,
          ]);
        } else {
          this.CampaignNameFormControl.removeValidators([Validators.required]);
          this.CampaignDescriptionFormControl.removeValidators([
            Validators.required,
          ]);
          this.CampaignActionDateFormControl.removeValidators([
            Validators.required,
          ]);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    const taskCreateformGroupChange = changes['taskCreateformGroup'];
    if (
      taskCreateformGroupChange?.currentValue?.value != null &&
      taskCreateformGroupChange.currentValue !==
        taskCreateformGroupChange.previousValue
    ) {
      this.formGroup = taskCreateformGroupChange.currentValue
        .value as UntypedFormGroup;
      this.formGroup.updateValueAndValidity();
    }

    if (changes?.initialClients?.currentValue?.length > 0) {
      const clients = changes.initialClients.currentValue;
      const ids = clients
        .map((c: any) => c.id ?? c.clt_id)
        .filter((id: number) => id != null);
      this.localClientIds = ids;
      this.clientIds$.next(ids);
    } else if (changes?.clientIds?.currentValue) {
      this.localClientIds = changes.clientIds.currentValue || [];
      this.clientIds$.next([...new Set(this.clientIds())]);
    } else {
      this.localClientIds = [];
      this.clientIds$.next([]);
    }
  }

  //#region get value
  get SelectedTaskValue(): TaskType | undefined {
    const val: any = this.SelectedTask.value;
    if (!val?.value) {
      return undefined;
    }
    switch (val.value) {
      case 'email':
        return TaskType.Email;
      case 'sms':
        return TaskType.Sms;
      case 'call':
        return TaskType.Call;
      case 'task':
        return TaskType.Task;
      case 'tour':
        return TaskType.Tour;
      default:
        return undefined;
    }
  }

  get SelectedPriorityValue(): TaskPriority {
    const val: any = this.SelectedPriority.value;
    if (!val?.value) {
      // Priority is required, but if somehow not set, default to low
      return TaskPriority.low;
    }
    switch (val.value) {
      case 'high':
        return TaskPriority.High;
      case 'medium':
        return TaskPriority.Med;
      case 'low':
        return TaskPriority.low;
      default:
        return TaskPriority.low;
    }
  }

  get SelectedClients(): number[] {
    return this.formGroup.controls.selectedClients.value || [];
  }

  get ScheduledDateTimeValue(): Date {
    const d: Date = this.ScheduleTime.value;
    return new Date(
      this.ScheduleDateValue.getFullYear(),
      this.ScheduleDateValue.getMonth(),
      this.ScheduleDateValue.getDate(),
      d.getHours(),
      d.getMinutes(),
    );
  }

  get ScheduleDateValue(): Date {
    const d: Date = this.ScheduleDate.value;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  }

  get ScheduleTimeValue(): string {
    const d: Date = this.ScheduleTime.value;
    return d.getHours() + ':' + d.getMinutes();
  }
  //#region get value

  //#region get
  get SelectedTaskCategory(): UntypedFormControl {
    return this.formGroup.controls.selectedTaskCategory as UntypedFormControl;
  }

  get SelectedTask(): UntypedFormControl {
    return this.formGroup.controls.selectedTask as UntypedFormControl;
  }

  get SelectedPriority(): UntypedFormControl {
    return this.formGroup.controls.selectedPriority as UntypedFormControl;
  }

  get SelectedAssigned(): UntypedFormControl {
    return this.formGroup.controls.selectedAssigned as UntypedFormControl;
  }

  get ScheduleDate(): UntypedFormControl {
    return this.formGroup.controls.scheduleDate as UntypedFormControl;
  }

  get ScheduleTime(): UntypedFormControl {
    return this.formGroup.controls.scheduleTime as UntypedFormControl;
  }

  get Title(): UntypedFormControl {
    return this.formGroup.controls.title as UntypedFormControl;
  }

  get Comment(): UntypedFormControl {
    return this.formGroup.controls.comment as UntypedFormControl;
  }

  get RRuleData(): RepeatRuleResponse {
    return this.rRuleObj || ({} as RepeatRuleResponse);
  }

  get isRruleSet(): boolean {
    return Boolean(this.rRuleObj?.RRule);
  }

  get CampaignNameFormControl(): UntypedFormControl {
    return this.formGroup.controls['campaign_name'] as UntypedFormControl;
  }

  get CampaignDescriptionFormControl(): UntypedFormControl {
    return this.formGroup.controls[
      'campaign_description'
    ] as UntypedFormControl;
  }

  get CampaignActionDateFormControl(): UntypedFormControl {
    return this.formGroup.controls[
      'campaign_action_date'
    ] as UntypedFormControl;
  }

  //#endregion get

  clearRRuleData() {
    this.rRuleObj = {} as RepeatRuleResponse;
  }

  setClient(e: any) {
    if (Array.isArray(e)) {
      this.clients = e;
    } else if (e?.clt_id > 0) {
      this.clients = [e];
    } else if (e?.id) {
      this.clients = [e];
    } else {
      this.clients = [];
    }

    const clientIds: number[] = this.clients.map((c) => {
      return c.clt_id ?? (c as any)?.id;
    });
    this.formGroup.controls.selectedClients.setValue(clientIds);
  }

  HideErrorPopup() {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
  }

  ClosePopUp() {
    this.closeDialogEvent();
  }

  closeCreateTaskPopup() {
    this.closeTaskPopup.emit({ closePopup: true });
    this.formGroup.reset();
    this.clearRRuleData();
    this.HidePopup.emit({
      closePopup: false,
      data: null,
      form: this.formGroup,
      uuid: this.uuid(),
      notificationTo: this.notificationTo(),
    });
  }

  private closeDialogEvent() {
    // Angular 21: Prevent multiple close events from being emitted
    // Only emit if dialog is actually visible
    const isVisible = this.showPopup();
    if (this.isClosing() || !isVisible) {
      return;
    }
    this.isClosing.set(true);
    this.ngZone.run(() => {
      this.formGroup.reset();
      this.clearRRuleData();
      // Emit single close event with proper data
      this.HidePopup.emit({
        closePopup: false,
        data: null,
        form: this.formGroup,
        uuid: this.uuid(),
        notificationTo: this.notificationTo(),
      });
      // Also emit closeTaskPopup for backward compatibility
      this.closeTaskPopup.emit({ closePopup: true });
    });
    // The effect in constructor will reset isClosing when dialog closes
  }

  setVariables(): void {
    this.showMiniSearch = this.clients.length == 0;
    const clientGroup = this.clientGroup();
    const initialClients = this.initialClients();
    const clientIds = this.clientIds();

    if (initialClients?.length > 0) {
      const ids = initialClients
        .map((c: any) => c.id ?? c.clt_id)
        .filter((id: number) => id != null);
      this.localClientIds = ids;
      this.clientIds$.next(ids);
      this.formGroup.controls.selectedClients.setValue(ids);
    } else if (clientGroup && clientGroup.length > 0) {
      this.localClientIds = [];
      clientGroup.forEach((val) => {
        this.clientIds().push(val.id);
      });
      this.showMiniSearch = false;
      this.formGroup.controls.selectedClients.setValue(clientIds);
    }

    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.store.dispatch({ type: 'ROUTER_GO' });
      }
    });

    if (!initialClients?.length && clientIds && clientIds.length > 0) {
      this.formGroup.controls.selectedClients.setValue(clientIds);
    }

    if (
      this.opportunityIds().length > 0 &&
      (clientIds?.length ?? 0) === 0 &&
      (initialClients?.length ?? 0) === 0
    ) {
      this.localClientIds = [0, 0];
    }
  }

  HideRRule(e: any): void {
    this.showRRule = e;
  }

  FetchRRuleData(e: any): void {
    this.rRuleObj = e;
  }

  SaveData() {
    this.formGroup.markAllAsTouched();
    this.markAllControlsAsDirty(Object.values(this.formGroup.controls));
    if (this.formGroup.valid) {
      this.btnLoading = true;
      const taskType = this.SelectedTaskValue;
      if (!taskType) {
        return; // Task type is required
      }
      const obj: TaskActionPayload = {
        assigned_to_type: TaskAssignToType.SpecificStaff,
        assigned_to_staff_id: this.SelectedAssigned.value.id,
        type: String(taskType),
        category_id: this.SelectedTaskCategory.value.id,
        category_name:
          this.SelectedTaskCategory.value.name_primary_language ||
          this.SelectedTaskCategory.value.name_secondary_language,
        title: this.Title.value,
        notes: this.Comment.value,
        priority: String(this.SelectedPriorityValue),
        scheduled_date: this.ScheduledDateTimeValue,
      };
      if (this.taskCreateType() == TaskCreateType.Automation) {
        const actions: Actions = {} as Actions;
        const data: ScheduleActionPayload = {
          schedule_in_min: 0,
          actions: actions,
          action_name: '',
          associated_staff: false,
          staff_ids: [],
          description: '',
        };
        obj.assigned_to_type = TaskAssignToType.ClientRep;
        obj.assigned_to_staff_id = 0;
        data.schedule_in_min = this.SelectedAutomationTimingFormControl.value
          ? this.SelectedAutomationTimingFormControl.value.value
          : 0;
        data.action_name =
          this.SelectedAutomationTimingFormControl.value?.key ??
          this.SelectedAutomationTimingTypeFormControl.value;
        data.associated_staff =
          this.SelectedAssociatedStaffFormControl?.value ?? false;
        data.staff_ids =
          this.SelectedAdditionalStaffFormControl?.value?.map(
            (staff: any) => staff.id,
          ) ?? [];
        data.actions.email_action_payload = null;
        data.actions.sms_action_payload = null;
        data.actions.whatsapp_action_payload = null;
        data.description = `<strong>${this.SelectedTaskCategory.value.name_primary_language} - </strong> ${this.Title.value}`;
        data.actions.task_action_payload = obj;
        this.HidePopup.emit({
          closePopup: true,
          data: null,
          form: null,
          uuid: '',
          notificationTo: SendNotificationTo.Client,
        });
        this.btnLoading = false;
        this.HidePopup.emit({
          closePopup: true,
          data: data,
          form: this.formGroup,
          uuid: this.uuid(),
          notificationTo: this.notificationTo(),
        });
      } else {
        this.HidePopup.emit({
          closePopup: true,
          data: null,
          form: null,
          uuid: '',
          notificationTo: SendNotificationTo.Client,
        });
        this.clearRRuleData();
        this.HidePopup.emit({
          closePopup: true,
          data: obj,
          form: this.formGroup,
          uuid: this.uuid(),
          notificationTo: this.notificationTo(),
        });
      }
    }
  }

  CreateTask(): void {
    this.btnLoading = true;
    this.formGroup.markAllAsTouched();
    this.markAllControlsAsDirty(Object.values(this.formGroup.controls));

    if (this.opportunityIds().length > 0 && this.clientIds().length === 0) {
      this.errorMessages.push('client required');
      this.showErrorPopup?.next(true);
      this.btnLoading = false;
      return;
    }

    if (this.taskCreateType() == TaskCreateType.Bulk) {
      this.SaveCampaign();
    } else if (this.rRuleObj?.RRule) {
      if (this.opportunityIds().length > 0) {
        this.CreateRecurringTaskByOpportunity();
      } else {
        this.CreateRecurringTaskByClient();
      }
      return;
    }

    if (this.opportunityIds().length > 0) {
      this.CreateTaskByOpportunity();
    } else {
      this.CreateTaskByClient();
    }
  }

  CreateTaskByClient(): void {
    this.btnLoading = true;
    const taskType = this.SelectedTaskValue;
    if (!taskType) {
      this.errorMessages.push('Task type is required');
      this.showErrorPopup?.next(true);
      this.btnLoading = false;
      return;
    }
    const obj: CreateTaskByClientRequest = {
      client_id: this.SelectedClients,
      assigned_to_type: TaskAssignToType.SpecificStaff,
      assigned_to_staff_id: this.SelectedAssigned.value.id,
      type: taskType,
      category_id: this.SelectedTaskCategory.value.id,
      title: this.Title.value,
      notes: this.Comment.value,
      priority: this.SelectedPriorityValue,
      scheduled_date: this.ScheduledDateTimeValue,
    };
    this.taskService.TaskCreateServiceByClient(this.tenantId, obj).subscribe({
      next: (obj: any) => {
        if (obj.task_id.length > 0) {
          this.translate
            .get('Shared.TASK.TASK-CREATE.TASK_CREATED_MESSAGE')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.HidePopup.emit({
            closePopup: true,
            data: null,
            form: null,
            uuid: '',
            notificationTo: SendNotificationTo.Client,
          });
          this.cd.detectChanges();
          this.closeTaskPopup.emit({ closePopup: true, refreshdata: true });
          this.cd.detectChanges();
        } else {
          this.errorMessages.push('Something went wrong');
          this.showErrorPopup?.next(true);
          this.btnLoading = false;
          this.cd.detectChanges();
        }
      },
      error: (error: any) => {
        this.btnLoading = false;
        this.toastMessageService.showError(error);
        this.cd.detectChanges();
      },
    });
  }

  CreateRecurringTaskByClient(): void {
    this.btnLoading = true;
    const taskType = this.SelectedTaskValue;
    if (!taskType) {
      this.errorMessages.push('Task type is required');
      this.showErrorPopup?.next(true);
      this.btnLoading = false;
      return;
    }
    const obj: CreateRecurringTaskByClientRequest = {
      client_id: this.SelectedClients,
      assigned_to_type: TaskAssignToType.SpecificStaff,
      assigned_to_staff_id: this.SelectedAssigned.value.id,
      type: taskType,
      category_id: this.SelectedTaskCategory.value.id,
      title: this.Title.value,
      notes: this.Comment.value,
      priority: this.SelectedPriorityValue,
      start_date: this.ScheduleDateValue,
      time: this.ScheduleTimeValue,
      repeat_rule: this.rRuleObj.RRule,
    };

    this.taskService
      .TaskCreateRecurringServiceByClient(this.tenantId, obj)
      .subscribe({
        next: (obj: any) => {
          if (obj.recurring_template_id.length > 0) {
            this.translate
              .get('Shared.TASK.TASK-CREATE.TASK_CREATED_MESSAGE')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.HidePopup.emit({
              closePopup: true,
              data: null,
              form: null,
              uuid: '',
              notificationTo: SendNotificationTo.Client,
            });
            this.cd.detectChanges();
            this.closeTaskPopup.emit({ closePopup: true, refreshdata: true });
            this.cd.detectChanges();
          } else {
            this.errorMessages.push('Something went wrong');
            this.showErrorPopup?.next(true);
            this.btnLoading = false;
            this.cd.detectChanges();
          }
        },
        error: (error: any) => {
          this.btnLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  CreateTaskByOpportunity(): void {
    this.btnLoading = true;
    const taskType = this.SelectedTaskValue;
    if (!taskType) {
      this.errorMessages.push('Task type is required');
      this.showErrorPopup?.next(true);
      this.btnLoading = false;
      return;
    }
    const obj: CreateTaskByOpportunityRequest = {
      opportunity_id: this.opportunityIds(),
      assigned_to_type: TaskAssignToType.SpecificStaff,
      assigned_to_staff_id: this.SelectedAssigned.value.id,
      type: taskType,
      category_id: this.SelectedTaskCategory.value.id,
      title: this.Title.value,
      notes: this.Comment.value,
      priority: this.SelectedPriorityValue,
      scheduled_date: this.ScheduledDateTimeValue,
    };

    this.taskService
      .TaskCreateServiceByOpportunity(this.tenantId, obj)
      .subscribe({
        next: (obj: any) => {
          this.btnLoading = false;
          if (obj.task_id.length > 0) {
            this.translate
              .get('Shared.TASK.TASK-CREATE.TASK_CREATED_MESSAGE')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.HidePopup.emit({
              closePopup: true,
              data: null,
              form: null,
              uuid: '',
              notificationTo: SendNotificationTo.Client,
            });
            this.cd.detectChanges();
          } else {
            this.errorMessages.push('Something went wrong');
            this.btnLoading = false;
            this.cd.detectChanges();
          }
          this.HidePopup.emit({
            closePopup: true,
            data: null,
            form: null,
            uuid: '',
            notificationTo: SendNotificationTo.Client,
          });
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.btnLoading = false;
          this.toastMessageService.showError(error);
          this.errorMessages.push(error.originalError.error.errorMessage);
          this.showErrorPopup?.next(true);
          this.cd.detectChanges();
        },
      });
  }

  CreateRecurringTaskByOpportunity(): void {
    this.btnLoading = true;
    const taskType = this.SelectedTaskValue;
    if (!taskType) {
      this.errorMessages.push('Task type is required');
      this.showErrorPopup?.next(true);
      this.btnLoading = false;
      return;
    }
    const obj: CreateRecurringTaskByOpportunityRequest = {
      opportunity_id: this.opportunityIds(),
      assigned_to_type: TaskAssignToType.SpecificStaff,
      assigned_to_staff_id: this.SelectedAssigned.value.id,
      type: taskType,
      category_id: this.SelectedTaskCategory.value.id,
      title: this.Title.value,
      notes: this.Comment.value,
      priority: this.SelectedPriorityValue,
      start_date: this.ScheduleDateValue,
      time: this.ScheduleTimeValue,
      repeat_rule: this.rRuleObj.RRule,
    };

    this.taskService
      .TaskCreateRecurringServiceByOpportunity(this.tenantId, obj)
      .subscribe({
        next: (obj: any) => {
          this.btnLoading = false;
          if (obj.recurring_template_id.length > 0) {
            this.translate
              .get('Shared.TASK.TASK-CREATE.TASK_CREATED_MESSAGE')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.HidePopup.emit({
              closePopup: true,
              data: null,
              form: null,
              uuid: '',
              notificationTo: SendNotificationTo.Client,
            });
            this.cd.detectChanges();
            this.closeTaskPopup.emit({ closePopup: true, refreshdata: true });
            this.cd.detectChanges();
          } else {
            this.errorMessages.push('Something went wrong');
            this.showErrorPopup?.next(true);
            this.btnLoading = false;
            this.cd.detectChanges();
          }
        },
        error: (error: any) => {
          this.btnLoading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  SaveCampaign() {
    const payloadRequest: CampaignRequest[] = [] as CampaignRequest[];
    const payload: CampaignRequest = {} as CampaignRequest;
    const formValues = this.formGroup.value;
    payload.campaign_name = formValues['campaign_name'];
    payload.campaign_isvisble = formValues['is_visible'];
    payload.campaign_description = formValues['campaign_description'];
    payload.tenant_id = this.tenantId;
    payload.campaign_ownership = OwnershipLevel.Tenant;
    payload.campaign_franchise = 0;
    payload.actions_group = {} as CampaignActionsGroup;
    const actionGroups: CampaignActionsGroup = {} as CampaignActionsGroup;
    actionGroups.campaign_action_date = payload.campaign_isvisble
      ? this.timeService.ConvertLocalDateTimeToUTC(
          formValues['campaign_action_date'],
        )
      : new Date().toISOString();
    actionGroups.source_data_type = this.CampaignSource();
    const sourceDataExtractionRules: SourceDataExtractionRules =
      {} as SourceDataExtractionRules;
    const CampaignSourceValue = this.CampaignSource();
    if (CampaignSourceValue == CampaignSource.Clients) {
      sourceDataExtractionRules.clients = this.clientIds();
      sourceDataExtractionRules.reports = null;
      sourceDataExtractionRules.rule = null;
      sourceDataExtractionRules.jsonrule = null;
    } else if (CampaignSourceValue == CampaignSource.Report) {
      const reports: Reports = {} as Reports;
      const selectedReportData = this.ReportDataForCampaign().value;
      reports.payload = selectedReportData.rpt_fltr_payload;
      reports.relative_date = selectedReportData.rpt_fltr_relative_date_range;
      reports.report_key = selectedReportData.report_key;
      sourceDataExtractionRules.reports = reports;
      sourceDataExtractionRules.clients = null;
      sourceDataExtractionRules.rule = null;
      sourceDataExtractionRules.jsonrule = null;
    } else {
      sourceDataExtractionRules.clients = null;
      sourceDataExtractionRules.reports = null;
      sourceDataExtractionRules.rule = null;
      sourceDataExtractionRules.jsonrule = null;
    }
    actionGroups.source_data_extraction_rules = sourceDataExtractionRules;
    actionGroups.action_payload = [] as AutomationActionPayload[];
    const actionPayloads: AutomationActionPayload = {
      actions: {
        client_email_action_payload: null,
        client_sms_action_payload: null,
        client_whatsapp_action_payload: null,
        staff_email_action_payload: null,
        staff_sms_action_payload: null,
        staff_whatsapp_action_payload: null,
        staff_task_action_payload: null,
        client_pushnotification_action_payload: null,
        staff_pushnotification_action_payload: null,
        notification_staff: null,
      },
      schedule_in_min: 0,
    };
    actionPayloads.actions.staff_sms_action_payload = null;
    actionPayloads.actions.client_sms_action_payload = null;
    actionPayloads.actions.staff_whatsapp_action_payload = null;
    actionPayloads.actions.client_whatsapp_action_payload = null;
    actionPayloads.actions.staff_email_action_payload = null;
    actionPayloads.actions.client_email_action_payload = null;
    const notificationToStaff: NotificationToStaff = {} as NotificationToStaff;
    notificationToStaff.notify_additional_staff_to = [];
    notificationToStaff.notify_record_associated_staff = true;
    actionPayloads.actions.notification_staff = notificationToStaff;
    const taskType = this.SelectedTaskValue;
    if (!taskType) {
      this.errorMessages.push('Task type is required');
      this.showErrorPopup?.next(true);
      this.btnLoading = false;
      return;
    }
    const taskActionPayload: TaskActionPayload = {} as TaskActionPayload;
    taskActionPayload.assigned_to_type = TaskAssignToType.SpecificStaff;
    taskActionPayload.assigned_to_staff_id = this.SelectedAssigned.value.id;
    taskActionPayload.type = String(taskType);
    taskActionPayload.category_id = this.SelectedTaskCategory.value.id;
    taskActionPayload.title = this.Title.value;
    taskActionPayload.notes = this.Comment.value;
    taskActionPayload.priority = String(this.SelectedPriorityValue);
    taskActionPayload.scheduled_date =
      this.timeService.ConvertLocalDateTimeToUTC(this.ScheduledDateTimeValue);
    taskActionPayload.repeat_rule = this.rRuleObj.RRule;
    actionPayloads.actions.staff_task_action_payload = taskActionPayload;
    actionGroups.action_payload.push(actionPayloads);
    payload.actions_group = actionGroups;
    payloadRequest.push(payload);
    this.campaignService.CreateCampaigns(payloadRequest).subscribe({
      next: () => {
        this.translate
          .get('TASK.TASK-CREATE.TASK_CREATED_MESSAGE')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        this.btnLoading = false;
        this.cd.detectChanges();
        this.ClosePopUp();
      },
      error: (error) => {
        this.toastMessageService.showError(error);
        this.btnLoading = false;
        this.cd.detectChanges();
      },
    });
  }

  getTomorrowDate(): string {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowDate = tomorrow.toISOString();
    return tomorrowDate;
  }

  onRemoveClient(index: number) {
    const clientGroup = this.clientGroup();
    if (clientGroup[index]) clientGroup.splice(index, 1);
  }

  findInvalidControls() {
    const invalid = [];
    const controls = this.formGroup.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  public markAllControlsAsDirty(abstractControls: AbstractControl[]): void {
    abstractControls.forEach((abstractControl) => {
      if (abstractControl instanceof UntypedFormControl) {
        (abstractControl as UntypedFormControl).markAsDirty({ onlySelf: true });
      } else if (abstractControl instanceof UntypedFormGroup) {
        this.markAllControlsAsDirty(
          Object.values((abstractControl as UntypedFormGroup).controls),
        );
      } else if (abstractControl instanceof UntypedFormArray) {
        this.markAllControlsAsDirty(
          (abstractControl as UntypedFormArray).controls,
        );
      }
    });
  }

  createProspect() {
    this.showCreateProspectPopup$.next(true);
  }

  get IsVisibleFormControl(): UntypedFormControl {
    return this.formGroup.controls['is_visible'] as UntypedFormControl;
  }

  openclientsidebar(event: any) {
    this.openClientProfile.emit(event);
  }

  closeSidebar(event: any) {
    this.showClientSidebar = false;
    this.openClientProfile.emit(event);
  }

  hidePopup() {
    this.showCreateProspectPopup$.next(false);
  }

  get SelectedAutomationTimingTypeFormControl() {
    return this.formGroup.controls[
      'automation_timings_type'
    ] as UntypedFormControl;
  }

  get SelectedAutomationTimingFormControl() {
    return this.formGroup.controls['automation_timing'] as UntypedFormControl;
  }

  get SelectedAutomationScheduleTypeFormControl() {
    return this.formGroup.controls[
      'automation_schedule_type'
    ] as UntypedFormControl;
  }

  get SelectedAdditionalStaffFormControl() {
    return this.formGroup.controls[
      'notify_additional_staff_to'
    ] as UntypedFormControl;
  }

  get SelectedAssociatedStaffFormControl() {
    return this.formGroup.controls[
      'notify_record_associated_staff'
    ] as UntypedFormControl;
  }

  public get getTaskCreateType() {
    return TaskCreateType;
  }

  public get SendNotificationTo() {
    return SendNotificationTo;
  }
}
